import { useCallback, useEffect, useRef, useState } from 'react'

import { admin } from '../../lib/api/admin.ts'
import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { artifacts, machines } from '../../lib/api/resources.ts'
import type { ArtifactDetail } from '../../lib/api/types.ts'
import { formatBytes, readSnapshot } from '../../lib/artifacts/snapshot.ts'
import { useAuth } from '../../lib/auth/AuthContext.tsx'
import { isConfirmed, openCommandIdOf, unavailableReasonText } from '../../lib/commands/semantics.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import { formatDayClock } from '../../lib/format.ts'
import { useAction } from '../../lib/useAction.ts'
import { useResource } from '../../lib/useResource.ts'
import { CommandStateBadge } from '../commands/CommandState.tsx'
import { Button } from '../primitives/Button.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { SelectField } from '../primitives/Field.tsx'
import { Pagination } from '../primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'

const TYPE_OPTIONS = [
  { value: '', label: 'todos os tipos' },
  { value: 'system_snapshot', label: 'system_snapshot' },
  { value: 'web_defense', label: 'web_defense' },
  { value: 'event_log', label: 'event_log' },
  { value: 'memory_dump', label: 'memory_dump' },
  { value: 'file', label: 'file' },
]

/** O que se pede ao agente pela tela, e o que cada um traz. */
const COLLECTABLE = {
  system_snapshot: 'RAM, CPU, consumo do agente, fila e conexões.',
  web_defense: 'integração com o nginx (versão, servers plugados, avisos) e o registro do Web Defense (bloqueios, challenges, exceções, reincidências).',
} as const

type CollectableArtifact = keyof typeof COLLECTABLE

/**
 * Aba "Artefatos" da máquina. Coletar é staff e admin; ler o que foi coletado é
 * SÓ `platform_admin` (staff não vê a lista). Pede-se `system_snapshot` ou
 * `web_defense`.
 */
export function MachineArtifactsDialog({
  machine,
  onClose,
}: {
  machine: { id: string; label: string }
  onClose: () => void
}) {
  const { isAdmin } = useAuth()
  const [openId, setOpenId] = useState<string | null>(null)
  // Muda quando um comando de coleta é confirmado: a lista se relê.
  const [listNonce, setListNonce] = useState(0)
  const bump = useCallback(() => setListNonce((n) => n + 1), [])

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Artefatos"
      description={machine.label}
      wide="xl"
    >
      {openId !== null ? (
        <ArtifactView id={openId} onBack={() => setOpenId(null)} />
      ) : (
        <div className="space-y-4">
          <CollectCard machineId={machine.id} onOpenArtifact={isAdmin ? setOpenId : undefined} onCollected={bump} />
          {isAdmin ? (
            <ArtifactList machineId={machine.id} reloadKey={listNonce} onOpen={setOpenId} />
          ) : (
            <Notice tone="neutral">
              Os artefatos coletados só são visíveis para administradores da plataforma.
            </Notice>
          )}
        </div>
      )}
    </Dialog>
  )
}

function CollectCard({
  machineId,
  onOpenArtifact,
  onCollected,
}: {
  machineId: string
  onOpenArtifact?: (id: string) => void
  onCollected: () => void
}) {
  const action = useAction()
  const [commandId, setCommandId] = useState<string | null>(null)
  const [initial, setInitial] = useState<AgentCommand | null>(null)
  const [artifact, setArtifact] = useState<CollectableArtifact>('system_snapshot')

  const catalogFetcher = useCallback((signal: AbortSignal) => admin.commandCatalog(machineId, signal), [machineId])
  const catalog = useResource(catalogFetcher)
  const def = catalog.data?.commands.find((c) => c.kind === 'collect_artifact')
  const unavailable = def !== undefined && !def.available

  const collect = (): void =>
    action.run(async () => {
      try {
        // Uma chave por CLIQUE: repetir devolve o mesmo comando, não cria outro.
        // `POST /machines/{id}/commands` responde 405; vai pela rota de gestão.
        const command = await admin.enqueueCommand(machineId, {
          kind: 'collect_artifact',
          params: JSON.stringify({ artifact }),
          reason: null,
          idempotencyKey: crypto.randomUUID(),
          acknowledgeNotDeliverable: false,
        })
        setInitial(command)
        setCommandId(command.id)
      } catch (cause) {
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            setInitial(null)
            setCommandId(id)
            return
          }
        }
        throw cause
      }
    })

  return (
    <section className="space-y-2 border border-line-subtle p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-fg">Coletar artefato</h3>
          <p className="text-micro text-fg-muted">{COLLECTABLE[artifact]}</p>
        </div>
        <div className="flex items-end gap-2">
          <SelectField
            label="Artefato"
            value={artifact}
            onValueChange={(value) => setArtifact(value as CollectableArtifact)}
            options={Object.keys(COLLECTABLE).map((value) => ({ value, label: value }))}
            className="w-44"
          />
          <Button variant="primary" busy={action.busy} disabled={unavailable} onClick={collect}>
            Coletar artefato
          </Button>
        </div>
      </div>

      {unavailable && <Notice tone="warn">{unavailableReasonText(def.unavailable_reason)}</Notice>}
      {action.error !== null && (
        <p role="alert" className="text-sm text-crit">
          {describeError(action.error)}
        </p>
      )}

      {commandId !== null && (
        <CollectTracking
          key={commandId}
          machineId={machineId}
          commandId={commandId}
          initial={initial}
          onOpenArtifact={onOpenArtifact}
          onCollected={onCollected}
        />
      )}
    </section>
  )
}

function CollectTracking({
  machineId,
  commandId,
  initial,
  onOpenArtifact,
  onCollected,
}: {
  machineId: string
  commandId: string
  initial: AgentCommand | null
  onOpenArtifact?: (id: string) => void
  onCollected: () => void
}) {
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  const state = command?.state ?? null
  const confirmed = state !== null && isConfirmed(state)
  const [artifactId, setArtifactId] = useState<string | null>(null)
  const announced = useRef(false)

  // Depois do `executed`, `command_id` liga o comando ao artefato da lista.
  useEffect(() => {
    if (!confirmed || announced.current) return
    announced.current = true
    onCollected()
    if (onOpenArtifact === undefined) return
    machines
      .artifacts(machineId, { page: 1, pageSize: 20 })
      .then((page) => setArtifactId(page.items.find((a) => a.command_id === commandId)?.id ?? null))
      .catch(() => setArtifactId(null))
  }, [confirmed, machineId, commandId, onCollected, onOpenArtifact])

  return (
    <div className="space-y-1.5 border-t border-line-subtle pt-2 text-sm">
      {command === null ? (
        error !== null ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <LoadingRow label="Lendo o comando…" />
        )
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <CommandStateBadge state={command.state} />
          {state === 'failed' && command.failure_detail !== null && (
            <span className="text-micro text-crit">{command.failure_detail}</span>
          )}
          {state === 'expired' && (
            <span className="text-micro text-warn">Expirou. Colete de novo.</span>
          )}
          {confirmed && onOpenArtifact !== undefined && artifactId !== null && (
            <Button size="sm" variant="primary" onClick={() => onOpenArtifact(artifactId)}>
              Ver artefato
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function ArtifactList({
  machineId,
  reloadKey,
  onOpen,
}: {
  machineId: string
  reloadKey: number
  onOpen: (id: string) => void
}) {
  const [page, setPage] = useState(1)
  const [type, setType] = useState('')

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      machines.artifacts(machineId, { artifact: type === '' ? undefined : type, page, pageSize: 20 }, signal),
    // reloadKey relê a lista quando uma coleta termina.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [machineId, type, page, reloadKey],
  )
  const { data, error, loading, reload } = useResource(fetcher)

  return (
    <section className="space-y-2">
      <div className="flex items-end justify-between gap-2">
        <h3 className="text-micro font-medium uppercase tracking-label text-fg-secondary">Coletados</h3>
        <SelectField
          label="Tipo"
          value={type}
          onValueChange={(value) => {
            setType(value)
            setPage(1)
          }}
          options={TYPE_OPTIONS}
          className="w-48"
        />
      </div>

      {error !== null ? (
        <ErrorState error={error} onRetry={reload} />
      ) : data === null ? (
        <LoadingRow />
      ) : data.items.length === 0 ? (
        <EmptyState title="Nenhum artefato coletado." />
      ) : (
        <>
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Coletado
                </th>
                <th scope="col" className={TH}>
                  Tipo
                </th>
                <th scope="col" className={TH}>
                  Tamanho
                </th>
                <th scope="col" className={TH}>
                  SHA-256
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id} className={TR}>
                  <td className={`${TD} tnum whitespace-nowrap`}>{formatDayClock(item.collected_at)}</td>
                  <td className={`${TD} font-mono`}>{item.artifact}</td>
                  <td className={`${TD} tnum whitespace-nowrap`}>{formatBytes(item.size_bytes)}</td>
                  <td className={`${TD} font-mono text-micro text-fg-muted`} title={item.sha256}>
                    {item.sha256.slice(0, 12)}…
                  </td>
                  <td className={`${TD} text-right`}>
                    <Button size="sm" onClick={() => onOpen(item.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            totalCount={data.totalCount}
            totalPages={data.totalPages}
            busy={loading}
            onPageChange={setPage}
          />
        </>
      )}
    </section>
  )
}

function ArtifactView({ id, onBack }: { id: string; onBack: () => void }) {
  const fetcher = useCallback((signal: AbortSignal) => artifacts.get(id, signal), [id])
  const { data, error, reload } = useResource<ArtifactDetail>(fetcher)

  return (
    <div className="space-y-3">
      <Button variant="ghost" onClick={onBack}>
        ← Artefatos
      </Button>
      {error !== null ? (
        <ErrorState error={error} onRetry={reload} />
      ) : data === null ? (
        <LoadingRow />
      ) : (
        <ArtifactBody detail={data} />
      )}
    </div>
  )
}

function ArtifactBody({ detail }: { detail: ArtifactDetail }) {
  const { summary, content, text } = detail
  const hasContent = content !== null && content !== undefined
  const sections = summary.artifact === 'system_snapshot' ? readSnapshot(content) : null

  const download = (): void => {
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${summary.artifact}-${summary.id.slice(0, 8)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-fg-secondary">
          <span className="font-mono text-fg">{summary.artifact}</span> · coletado em{' '}
          {formatDayClock(summary.collected_at)} · recebido em {formatDayClock(summary.received_at)} ·{' '}
          {formatBytes(summary.size_bytes)}
        </p>
        {hasContent && (
          <Button size="sm" onClick={download}>
            Baixar JSON
          </Button>
        )}
      </div>
      <p className="font-mono text-micro text-fg-muted break-all">sha256 {summary.sha256}</p>

      {sections !== null ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {sections.map((section) => (
            <section key={section.title} className="border border-line-subtle p-3">
              <h4 className="mb-2 text-micro font-medium uppercase tracking-label text-fg-secondary">
                {section.title}
              </h4>
              <dl className="space-y-1 text-sm">
                {section.rows.map((row) => (
                  <div key={row.label} className="flex justify-between gap-3">
                    <dt className="text-fg-muted">{row.label}</dt>
                    <dd className="tnum text-right text-fg">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      ) : text !== null ? (
        <pre className="max-h-96 overflow-auto bg-inset p-2 font-mono text-micro text-fg">{text}</pre>
      ) : hasContent ? (
        <pre className="max-h-96 overflow-auto bg-inset p-2 font-mono text-micro text-fg">
          {JSON.stringify(content, null, 2)}
        </pre>
      ) : (
        <Notice tone="neutral">Conteúdo binário.</Notice>
      )}
    </div>
  )
}
